import React, { useEffect, useMemo, useState } from "react";
import "./recovery.css";

const BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const SUPER = `${BASE}/api/superadmin/`;
const getCsrf = () => { const v = (document.cookie.match(/(?:^|; )csrftoken=([^;]*)/) || [])[1]; return v ? decodeURIComponent(v) : ""; };
async function jsonRequest(url) {
  const response = await fetch(url, { credentials: "include", cache: "no-store" });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || `Unable to load accounts (${response.status})`);
  return data;
}

export default function RecoveryResetPanel({ mode = "platform", restaurantSlug }) {
  const isOwner = mode === "owner";
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantId, setRestaurantId] = useState("");
  const [people, setPeople] = useState([]);
  const [target, setTarget] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const chosen = useMemo(() => restaurants.find(r => String(r.id) === String(restaurantId)), [restaurants, restaurantId]);
  const slug = isOwner ? restaurantSlug : chosen?.slug;

  useEffect(() => {
    if (isOwner) return;
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const all = [];
        for (let page = 1; page <= 400; page++) {
          const data = await jsonRequest(`${SUPER}restaurants/?page=${page}`);
          const batch = Array.isArray(data) ? data : (data.results || []);
          all.push(...batch);
          if (Array.isArray(data) || !batch.length || all.length >= (data.count || 0)) break;
        }
        if (live) setRestaurants(all);
      } catch (e) { if (live) setError(e.message); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [isOwner]);

  useEffect(() => {
    setTarget(""); setPeople([]); setResult(null);
    if (!slug) return;
    let live = true;
    (async () => {
      setLoading(true); setError("");
      try {
        if (!isOwner) {
          const data = await jsonRequest(`${SUPER}restaurants/${encodeURIComponent(restaurantId)}/owners/`);
          if (live) setPeople((Array.isArray(data) ? data : data.results || []).filter(x => x.is_active));
        } else {
          // Owner/Manager staff listing depends on the restaurant operations API response.
          const data = await jsonRequest(`${BASE}/api/operations/${encodeURIComponent(slug)}/admin/staff/`);
          const list = Array.isArray(data) ? data : (data.results || data.staff || []);
          if (live) setPeople(list.filter(x => String(x.role).toUpperCase() === "MANAGER" && x.is_active !== false)
            .map(x => ({...x, user_id: x.user_id ?? x.user?.id, username: x.username ?? x.user?.username ?? x.name}))
            .filter(x => x.user_id));
        }
      } catch (e) { if (live) setError(e.message); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [isOwner, restaurantId, slug]);

  async function submit(e) {
    e.preventDefault(); setError(""); setResult(null);
    if (!confirm || !target || !slug || otp.length !== 6) return;
    setBusy(true);
    try {
      // Initialize the CSRF cookie if the browser has not received it yet.
      if (!getCsrf()) await jsonRequest(isOwner ? `${BASE}/api/operations/csrf/` : `${SUPER}csrf/`);
      const response = await fetch(`${BASE}/api/security/2fa/recovery/reset/`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrf() },
        body: JSON.stringify({ target_user_id: Number(target), restaurant_slug: slug, actor_password: password, actor_otp: otp })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || `Reset failed (${response.status})`);
      setResult(data); setPassword(""); setOtp(""); setTarget(""); setConfirm(false);
    } catch (e) { setError(e.message || "Reset failed"); }
    finally { setBusy(false); }
  }

  return <section className="fh-recovery-panel">
    <div className="fh-recovery-heading"><div className="fh-recovery-icon">✦</div><div>
      <p className="fh-recovery-kicker">ACCOUNT SECURITY</p><h2>Lost authenticator recovery</h2>
      <p>Select the restaurant and {isOwner ? "Manager" : "Owner"} who needs their authenticator reset.</p>
    </div></div>
    <div className="fh-recovery-warning"><strong>Restricted action</strong><p>
      {isOwner ? "Only Managers in your restaurant are eligible." : "Platform Super Admins can reset restaurant Owners. Privileged counterpart recovery requires a separately authorized workflow."}
      {" "}The server enforces permissions, verifies a fresh OTP, revokes sessions and requires new authenticator enrollment.
    </p></div>
    <form className="fh-recovery-form" onSubmit={submit}>
      {isOwner ? <p className="fh-recovery-note">Restaurant: <strong>{restaurantSlug || "Not available"}</strong></p> :
        <label>Restaurant<select required value={restaurantId} onChange={e => setRestaurantId(e.target.value)}>
          <option value="">Select a restaurant</option>
          {restaurants.map(r => <option key={r.id} value={r.id}>{r.name} ({r.slug})</option>)}
        </select></label>}
      <p className="fh-recovery-note">{!isOwner && `${restaurants.length} restaurant(s) loaded. `}{people.length} eligible {isOwner ? "Manager" : "Owner"}(s) for the selected restaurant.</p>
      <label>{isOwner ? "Manager" : "Restaurant Owner"}<select required value={target} onChange={e => setTarget(e.target.value)} disabled={!slug || loading}>
        <option value="">{loading ? "Loading accounts…" : `Select ${isOwner ? "Manager" : "Owner"}`}</option>
        {people.map(p => <option key={p.user_id} value={p.user_id}>{p.username || p.email || `Account ${p.user_id}`}{p.email ? ` (${p.email})` : ""}</option>)}
      </select></label>
      <label>Your current password<input type="password" required autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} /></label>
      <label>Fresh six-digit authenticator code<input type="text" required inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} /></label>
      <label className="fh-recovery-check"><input type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)} /><span>I verified this person's identity. Their authenticator devices, recovery codes and known sessions will be revoked.</span></label>
      {error && <p role="alert" className="fh-recovery-error">{error}</p>}
      {result && <p role="status" className="fh-recovery-success">Authenticator reset successful. The user must enroll a new authenticator. Audit ID: {result.audit_id}. Sessions revoked: {result.sessions_revoked}.</p>}
      <button className="fh-recovery-primary" disabled={busy || loading || !slug || !target || !confirm || !password || otp.length !== 6}>{busy ? "Resetting securely…" : "Reset authenticator"}</button>
    </form>
  </section>;
}